import React, { useEffect, useState } from 'react';
/**
 * `PersonalDetailPage` captures personal details for a journey.
 *
 * Collected fields:
 * - Title (via `TitleSelectInput` unless readonly)
 * - First name / surname
 * - Date of birth (via `DateSelector`, with min/max DOB range and age checks)
 * - Email (and optional confirm email)
 * - Phone number
 *
 * Validation:
 * - Runs client-side validation when `validate` is toggled and reports overall
 *   validity via `onValidated`.
 * - Emits current details when `update` is toggled via `onUpdate`.
 * - Enforces max-lengths and format validation (email) via `CommonValidation`.
 * - Uses `isAgeValid` to enforce minimum age (18) when DOB is mandatory.
 *
 * UI:
 * - Uses Material UI tooltips for help text.
 * - Readonly flags can lock title and/or name fields.
 */
import dayjs from 'dayjs';
import Tooltip from '@mui/material/Tooltip';
import { PersonalDetailType } from '../CommonTypes';
import DateSelector from '../DateSelector/DateSelector';
import TitleSelectInput from './TitleSelectInput';
import { CommonValidation } from '../CommonValidation/commonValidation';
import { SelectInputHelper } from '../SelectInput/SelectInput';
import TextInput from '../TextInput/TextInput';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../app-state/Hooks';
import { isAgeValid } from '../HelperMethods';

type PersonalDetailProps = {
  initialPersonalDetail: PersonalDetailType;
  validate: boolean;
  update: boolean;
  token: string;
  isNameReadonly?: boolean;
  isTitleReadonly?: boolean;
  showEmailConfirmation: boolean;
  emailTooltipHeader?: string;
  phoneNumberTooltipHeader?: string;
  dobTooltipHeader?: string;
  onValidated: (isValid: boolean) => void;
  onUpdate: (personalDetails: PersonalDetailType) => void;
  isDoBOptional: boolean;
};

export type PersonalDetailErrors = {
  title: string;
  firstName: string;
  surname: string;
  dateOfBirth: string;
  email: string;
  confirmEmail: string;
  phone: string;
};

const initialErrors = {
  title: '',
  firstName: '',
  surname: '',
  dateOfBirth: '',
  email: '',
  confirmEmail: '',
  phone: ''
};

const PersonalDetailPage = ({
  initialPersonalDetail,
  validate,
  update,
  token,
  isNameReadonly,
  isTitleReadonly,
  showEmailConfirmation,
  emailTooltipHeader,
  phoneNumberTooltipHeader,
  dobTooltipHeader,
  onValidated,
  onUpdate,
  isDoBOptional
}: PersonalDetailProps): JSX.Element => {
  const cms = useAppSelector((s) => s.content.customerPage);
  const appConfig = useAppSelector((s) => s.appConfig);
  const [personalDetail, setPersonalDetail] = useState<PersonalDetailType>(
    initialPersonalDetail
  );
  const [errors, setErrors] = useState<PersonalDetailErrors>(initialErrors);
  const [dobIsValid, setDobIsValid] = useState(true);
  const [updateDobError, setUpdateDobError] = useState(false);

  const { title, firstName, surname, dateOfBirth, email, confirmEmail, phone } =
    personalDetail;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const firstNameCharLimit = 45;
  const surnameCharLimit = 50;
  const phoneCharLimit = 15;
  const emailCharLimit = 50;

  const { t } = useTranslation();
  const titleLabel = t('personal-details.title-label');
  const firstNameLabel = t('personal-details.first-name-label');
  const surnameLabel = t('personal-details.surname-label');
  const dateOfBirthLabel = t('personal-details.date-of-birth-label');
  const emailAddressLabel = t('personal-details.email-address-label');
  const toolTipLabel = t('personal-details.tool-tip-text');
  const confirmEmailLabel = t('personal-details.confirm-email-address-label');
  const phoneNumberLabel =
    cms.phoneNumberLabel || t('personal-details.phone-number-label');
  const titleRequiredMsg = t('personal-details.title-required-validation');
  const firstnameRequiredMsg = t(
    'personal-details.firstname-required-validation'
  );
  const surnameRequiredMsg = t('personal-details.surname-required-validation');
  const dobRequiredMsg = t('personal-details.dob-required-validation');
  const dobInvalidMsg = t('personal-details.dob-invalid-validation');
  const over18Msg = t('personal-details.over-18-validation');
  const invalidEmailMsg = t(
    'personal-details.email-invalid-personal-details-validation'
  );

  const confirmEmailRequiredMsg = t(
    'personal-details.confim-email-required-validation'
  );
  const emailRequiredMsg = t('personal-details.email-required-validation');
  const emailMismatchMessage = t('personal-details.email-mismatch-validation');
  const phoneRequiredMsg = t('personal-details.phone-required-validation', {
    phoneNumberLabel: phoneNumberLabel
  });

  const firstNameLimitError = t('personal-details.length-exceeded', {
    charLimit: firstNameCharLimit
  });

  const surnameLimitError = t('personal-details.length-exceeded', {
    charLimit: surnameCharLimit
  });

  const emailLimitError = t('personal-details.length-exceeded', {
    charLimit: emailCharLimit
  });

  const phoneLimitError = t('personal-details.length-exceeded', {
    charLimit: phoneCharLimit
  });

  useEffect(() => {
    if (validate === true) {
      const isValid = validatePersonalDetails();

      if (isValid === false) {
        setUpdateDobError(!updateDobError);
      }
      onValidated(isValid);
    }
  }, [validate]);

  useEffect(() => {
    if (update === true) {
      onUpdate(personalDetail);
    }
  }, [update]);

  const onChangePersonalDetails = (
    e: React.FormEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>,
    key: string
  ) => {
    const newPersonalDetail = {
      ...personalDetail,
      [key]: (e?.target as HTMLInputElement)?.value ?? ''
    };

    setPersonalDetail(newPersonalDetail);
  };

  const onChangeTitle = (value: string) => {
    const newPersonalDetail = {
      ...personalDetail,
      title: value
    };

    setPersonalDetail(newPersonalDetail);
  };

  const onChangeDateOfBirth = (value?: Date) => {
    if (!value || !dayjs(value).isValid()) return;

    const newPersonalDetail = {
      ...personalDetail,
      dateOfBirth: value
    };

    setPersonalDetail(newPersonalDetail);

    const dateOfBirthError = isAgeValid(value, { minAge: 18 }) ? '' : over18Msg;
    if (dateOfBirthError !== errors.dateOfBirth) {
      setErrors({ ...errors, dateOfBirth: dateOfBirthError });
    }
    setUpdateDobError(!updateDobError);
  };

  const validatePersonalDetails = (): boolean => {
    let isValid = true;

    const error: PersonalDetailErrors = { ...initialErrors };

    if (SelectInputHelper.NullValue() == title) {
      isValid = false;
      error.title = titleRequiredMsg;
    }

    if (CommonValidation.isEmpty(firstName)) {
      isValid = false;
      error.firstName = firstnameRequiredMsg;
    } else if (!CommonValidation.isValidLength(firstName, firstNameCharLimit)) {
      isValid = false;
      error.firstName = firstNameLimitError;
    }

    if (CommonValidation.isEmpty(surname)) {
      isValid = false;
      error.surname = surnameRequiredMsg;
    } else if (!CommonValidation.isValidLength(surname, surnameCharLimit)) {
      isValid = false;
      error.surname = surnameLimitError;
    }

    if (!isDoBOptional) {
      if (dobIsValid === false) {
        isValid = false;
        error.dateOfBirth = dobInvalidMsg;
      } else if (CommonValidation.isEmpty(dateOfBirth)) {
        isValid = false;
        error.dateOfBirth = dobRequiredMsg;
      } else if (!isAgeValid(dateOfBirth, { minAge: 18 })) {
        isValid = false;
        error.dateOfBirth = over18Msg;
      }
    }

    if (CommonValidation.isNotEmpty(email)) {
      if (!CommonValidation.isValidEmail(email)) {
        error.email = invalidEmailMsg;
        isValid = false;
      } else {
        if (!CommonValidation.isValidLength(email, emailCharLimit)) {
          isValid = false;
          error.email = emailLimitError;
        }
      }
    } else {
      isValid = false;
      error.email = emailRequiredMsg;
    }

    if (showEmailConfirmation === true) {
      if (CommonValidation.isEmpty(confirmEmail)) {
        isValid = false;
        error.confirmEmail = confirmEmailRequiredMsg;
      } else if (email != confirmEmail) {
        isValid = false;
        error.confirmEmail = emailMismatchMessage;
      }
    }

    if (CommonValidation.isEmpty(phone)) {
      isValid = false;
      error.phone = phoneRequiredMsg;
    } else if (!CommonValidation.isValidLength(phone, phoneCharLimit)) {
      isValid = false;
      error.phone = phoneLimitError;
    }

    setErrors(error);

    return isValid;
  };

  const maxDob = dayjs().subtract(18, 'year').startOf('day').toDate();
  const minDob = dayjs().subtract(120, 'year').startOf('day').toDate();

  const validateConfirmEmail = () => {
    const error: PersonalDetailErrors = { ...initialErrors };

    if (CommonValidation.isEmpty(confirmEmail))
      error.confirmEmail = confirmEmailRequiredMsg;
    else if (email != confirmEmail) error.confirmEmail = emailMismatchMessage;

    setErrors(error);
  };

  return (
    <div>
      <div data-testid='container-form' className='form-left-border'>
        <div className='inline-control'>
          <label className='control-label' htmlFor='title'>
            {titleLabel}
          </label>
          <div className='control'>
            {isTitleReadonly === true ? (
              <TextInput
                placeholder={titleLabel}
                name='title'
                className='input-text w250'
                value={title}
                error={errors.title}
                readOnly={true}
              />
            ) : (
              <TitleSelectInput
                value={title}
                onSelectedValueChange={onChangeTitle}
                error={errors.title}
                token={token}
                countryCode={appConfig.countryCode}
              />
            )}
          </div>
        </div>

        <div className='flex-row'>
          <div className='inline-control'>
            <label className='control-label' htmlFor='first-name'>
              {firstNameLabel}
            </label>
            <div className='control'>
              <TextInput
                placeholder={firstNameLabel}
                name='first-name'
                className='input-text w250'
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  onChangePersonalDetails(e, 'firstName')
                }
                value={firstName}
                error={errors.firstName}
                readOnly={isNameReadonly}
              />
            </div>
          </div>

          <div className='inline-control'>
            <label className='control-label' htmlFor='surname'>
              {surnameLabel}
            </label>
            <div className='control'>
              <TextInput
                placeholder={surnameLabel}
                name='surname'
                className='input-text w250'
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  onChangePersonalDetails(e, 'surname')
                }
                value={surname}
                error={errors.surname}
                readOnly={isNameReadonly}
              />
            </div>
          </div>
        </div>

        <label className='control-label'>{dateOfBirthLabel}</label>
        <div className='control'>
          <DateSelector
            value={dateOfBirth}
            minDate={minDob}
            maxDate={maxDob}
            invalidDateMessage={errors.dateOfBirth}
            updateDobError={updateDobError}
            tooltipTitle={toolTipLabel}
            tooltipText={dobTooltipHeader}
            culture={appConfig.cultureCode}
            name='date-of-birth'
            onChange={(value) => onChangeDateOfBirth(value)}
            onValidated={(isValid) => setDobIsValid(isValid)}
            ariaLabel={dateOfBirthLabel}
          />
        </div>
        <label className='control-label' htmlFor='email-address'>
          {emailAddressLabel}
        </label>
        <div className='control'>
          <div className='email-address-control'>
            <TextInput
              placeholder={emailAddressLabel}
              name='email-address'
              className='input-text w250'
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                onChangePersonalDetails(e, 'email')
              }
              value={email}
              error={errors.email}
            />
            <Tooltip
              title={emailTooltipHeader}
              enterTouchDelay={0}
              arrow
              placement='right'>
              <p data-testid='email-tooltip'>{toolTipLabel}</p>
            </Tooltip>
          </div>
        </div>
        {showEmailConfirmation === true && (
          <>
            <label className='control-label' htmlFor='confirm-email-address'>
              {confirmEmailLabel}
            </label>
            <div className='control'>
              <TextInput
                placeholder={confirmEmailLabel}
                name='confirm-email-address'
                className='input-text w250'
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  onChangePersonalDetails(e, 'confirmEmail')
                }
                value={confirmEmail || ''}
                error={errors.confirmEmail}
                onLeave={validateConfirmEmail}
              />
            </div>
          </>
        )}

        <label className='control-label' htmlFor='phone-number'>
          {phoneNumberLabel}
        </label>
        <div className='control'>
          <div className='phone-number-control'>
            <TextInput
              placeholder={phoneNumberLabel}
              name='phone-number'
              className='input-text w250'
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                onChangePersonalDetails(e, 'phone')
              }
              value={phone}
              error={errors.phone}
            />

            <Tooltip
              title={phoneNumberTooltipHeader}
              enterTouchDelay={0}
              arrow
              placement='right'>
              <p data-testid='phone-number-tooltip'>{toolTipLabel}</p>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalDetailPage;
